import moment from "moment";
import { CastMetadataInfos } from "../../Components/MetadataInput/MetadataInputTypes/metadata-input-casts";
import { LocalizedCreditsMetadataInfos } from "../../Components/MetadataInput/MetadataInputTypes/metadata-input-credits-multi-localized";
import { LocalizedDateTimeMetadataInfos } from "../../Components/MetadataInput/MetadataInputTypes/metadata-input-date-multi-localized";
import { LocalizedTitleMetadataInfos } from "../../Components/MetadataInput/MetadataInputTypes/metadata-input-titles-multi-localized";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { DbCultureInfos } from "../../Core/Api/Types/db-culture-infos";
import { EnrichedMetadata } from "../../Core/Api/Types/enriched-metadata";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import { Language } from "../../Core/Api/Types/language";
import { SynopsisMetadataInfos } from "../../Core/Api/Types/synopsis-metadata-infos";

export enum BundleMetadataValidationStatus {
  Ok,
  Missing,
}

export type BundleMetadataValidation = {
  fieldId: number;
  status: BundleMetadataValidationStatus;
  optional: boolean;
  regex?: string | null;
};

export const isMetadataLocalized = (meta: IDbBaseMetadataValueInfos): boolean => {
  return (
    (meta.$type.toString().includes("Localized") ||
      meta.$type.toString().includes("Cast") ||
      meta.$type.toString().includes("Syno")) &&
    meta.fieldType !== BaseMetadataType.DbLocalizedLanguageCodeMetadataValueInfos
  );
};

export const checkValidationByCountry = (
  country: DbCultureInfos | null,
  metadata: EnrichedMetadata[],
  epNb?: number | null
): BundleMetadataValidation[] => {
  let meta = metadata.filter(
    (x) => (!country && !isMetadataLocalized(x.value!)) || (country && isMetadataLocalized(x.value!))
  );

  let checks: BundleMetadataValidation[] = [];

  for (let i = 0; i < meta.length; ++i) {
    let check: BundleMetadataValidation = {
      fieldId: meta[i].field!.id,
      status: BundleMetadataValidationStatus.Ok,
      optional: !meta[i].usage!.isMandatory,
      regex: meta[i].field!.regexValue,
    };

    switch (meta[i].field?.baseType) {
      // #region Localized
      case BaseMetadataType.DbSynopsesMetadataValueInfos:
        {
          let localValues = (meta[i].value!.value as SynopsisMetadataInfos[])
            .filter((x) => x.languageLocalizedCultureId === country?.id)
            .filter(
              (x) =>
                x.epNb === epNb || (epNb === undefined && x.epNb === null) || (x.epNb === undefined && epNb === null)
            );
          let ok = true;

          for (let v = 0; v < localValues.length; ++v) {
            if (
              !localValues[v].text ||
              (localValues[v].text && check.regex && !localValues[v].text?.match(check.regex))
            ) {
              ok = false;
              break;
            }
          }

          if (!ok) check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbCastsMetadataValueInfos:
        {
          let localValues = (meta[i].value!.value as CastMetadataInfos[])
            .filter((x) => x.languageLocalizedCultureId === country?.id)
            .filter(
              (x) =>
                x.epNb === epNb || (epNb === undefined && x.epNb === null) || (x.epNb === undefined && epNb === null)
            );
          let ok = true;

          for (let v = 0; v < localValues.length; ++v) {
            if (
              !localValues[v].functionEnum ||
              !localValues[v].name ||
              !localValues[v].lastname ||
              (check.regex && localValues[v].name && !localValues[v].name?.match(check.regex)) ||
              (check.regex && localValues[v].lastname && !localValues[v].lastname?.match(check.regex))
            ) {
              ok = false;
              break;
            }
          }

          if (!ok) check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbLocalizedDateTimeMultiMetadataValueInfos:
        {
          let localValues = (meta[i].value!.value as LocalizedDateTimeMetadataInfos[])
            .filter((x) => x.cultureId === country?.id)
            .filter(
              (x) =>
                x.epNb === epNb || (epNb === undefined && x.epNb === null) || (x.epNb === undefined && epNb === null)
            );
          let ok = true;
          if (localValues.length <= 0) ok = false;
          for (let v = 0; v < localValues.length; ++v) {
            let date = moment(localValues[v].value);
            if ((date.year() === 1 && date.month() === 0 && date.day() === 1) || !date.isValid()) {
              ok = false;
              break;
            }
          }

          if (!ok) check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbLocalizedLanguageCodeMultiMetadataValueInfos:
        {
          if (
            (meta[i].value!.value as Language[])
              .filter((x) => x.cultureId === country?.id)
              .filter(
                (x) =>
                  x.epNb === epNb || (epNb === undefined && x.epNb === null) || (x.epNb === undefined && epNb === null)
              ).length === 0
          )
            check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbLocalizedTitlesMultiMetadataValueInfos:
        {
          let localValues = (meta[i].value!.value as LocalizedTitleMetadataInfos[])
            .filter((x) => x.cultureId === country?.id)
            .filter(
              (x) =>
                x.epNb === epNb || (epNb === undefined && x.epNb === null) || (x.epNb === undefined && epNb === null)
            );

          let ok = localValues.length > 0;

          for (let v = 0; v < localValues.length; ++v) {
            if (
              !localValues[v].value ||
              localValues[v].value === "" ||
              (check.regex && localValues[v].value && !localValues[v].value?.match(check.regex))
            ) {
              ok = false;
              break;
            }
          }

          if (!ok) check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbLocalizedRatingMultiMetadataValueInfos:
        {
          // TODO
          let localValues = (meta[i].value!.value as any[])
            .filter((x) => x.system.cultureId === country?.id)
            .filter((x) => x.values?.find((y) => (y?.epNb ?? null) === (epNb ?? null)));
          let ok = true;

          if (!localValues || !(localValues.length > 0)) ok = false;

          if (!ok) check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbLocalizedBoolMultiMetadataValueInfos:
        {
          // Consider false is a correct, so always good!
        }
        break;
      case BaseMetadataType.DbLocalizedCreditsMultiMetadataValueInfos:
        {
          let localValues = (meta[i].value!.value as LocalizedCreditsMetadataInfos[])
            .filter((x) => x.cultureId === country?.id)
            .filter(
              (x) =>
                x.epNb === epNb || (epNb === undefined && x.epNb === null) || (x.epNb === undefined && epNb === null)
            );

          if (localValues.length === 0) check.status = BundleMetadataValidationStatus.Missing;

          let ok = true;

          for (let v = 0; v < localValues.length; ++v) {
            if (
              !localValues[v].value ||
              localValues[v].value === "" ||
              (check.regex && localValues[v].value && !localValues[v].value?.match(check.regex))
            ) {
              ok = false;
              break;
            }
          }

          if (!ok) check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      // #endregion Localized
      // #endregion Common
      case BaseMetadataType.DbLocalizedLanguageCodeMetadataValueInfos:
        {
          // Note: Even if base type contains "Localized", this field is in fact localized but for the globality and not per country
          if (!meta[i].value!.value || meta[i].value!.value === null)
            check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbStringMetadataValueInfos:
        {
          if (
            !meta[i].value?.value ||
            meta[i].value?.value === "" ||
            (check.regex && meta[i].value?.value && !meta[i].value?.value?.match(check.regex))
          )
            check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbStringMultiMetadataValueInfos:
        {
          if (
            !meta[i].value?.value ||
            meta[i].value?.value.length === 0 ||
            (check.regex && meta[i].value?.value && !meta[i].value?.value?.match(check.regex))
          )
            check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbGenreMetadataValueInfos:
        {
          if (!meta[i].value?.value) check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      case BaseMetadataType.DbGenreMultiMetadataValueInfos:
        {
          if (!meta[i].value?.value || meta[i].value?.value.length === 0)
            check.status = BundleMetadataValidationStatus.Missing;
        }
        break;
      // #endregion Common
    }

    checks.push(check);
  }
  return checks;
};
